<script setup lang="ts">
import { Deferred, Head, Link, router, useForm } from '@inertiajs/vue3'
import { Search, SlidersHorizontal, X } from 'lucide-vue-next'
import { Users } from 'lucide-vue-next'
import { computed, onUnmounted, ref, watch } from 'vue'



import AddressForm, { type AddressFields } from '@/components/AddressForm.vue'
import AppDataTable from '@/components/AppDataTable.vue'
import type { TableColumn } from '@/components/AppDataTable.vue'
import AppPagination from '@/components/AppPagination.vue'
import FormSection from '@/components/FormSection.vue'
import HeadingSmall from '@/components/HeadingSmall.vue'
import InputError from '@/components/InputError.vue'
import { type Comment } from '@/components/comments/CommentItem.vue'
import CommentThread from '@/components/comments/CommentThread.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import AppLayout from '@/layouts/AppLayout.vue'
import OrganizationLayout from '@/layouts/organizations/Layout.vue'
import {
  editOrganizationPath,
  editPartnerPath,
  organizationAddressPath,
  organizationCommentsPath,
  organizationPath,
  organizationsPath,
} from '@/routes'
import { type BreadcrumbItem } from '@/types'

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Organization {
  id: number
  name: string
  contact: string
  email: string
  phone: string
  active: boolean
}

interface Address {
  id?: number
  unit_no: string | null
  street_no: string
  street_name: string
  street_type: string
  city: string
  state: string
  postcode: string
}

interface Partner {
  id: number
  name: string
  contact: string
  email: string
  phone: string
  active: boolean
}

interface PartnerFilters {
  q?: string | null
  active?: string | null
}

const props = defineProps<{
  organization: Organization
  address?: Address | null
  partners?: { records: Partner[]; pagination: PaginationMeta; filters: PartnerFilters } | null
  comments?: Comment[] | null
}>()

const breadcrumbs: BreadcrumbItem[] = [
  { title: 'Organizations', href: organizationsPath() },
  {
    title: props.organization.name,
    href: editOrganizationPath(props.organization.id),
  },
]

// ── Section switching — persisted in URL so pagination doesn't lose the tab ──
const urlSection = new URLSearchParams(window.location.search).get('section')
const validSections = ['details', 'address', 'partners'] as const
type Section = (typeof validSections)[number]
const activeSection = ref<Section>(
  validSections.includes(urlSection as Section) ? (urlSection as Section) : 'details',
)

// ── Details form ──────────────────────────────────────────────────────────────
const detailsForm = useForm({
  name: props.organization.name,
  contact: props.organization.contact,
  email: props.organization.email,
  phone: props.organization.phone,
  active: props.organization.active,
})

function submitDetails() {
  detailsForm.patch(organizationPath(props.organization.id), {
    preserveScroll: true,
  })
}

// ── Address form — initialized once the deferred prop arrives ─────────────────
const addressForm = useForm({
  unit_no: '',
  street_no: '',
  street_name: '',
  street_type: '',
  city: '',
  state: '',
  postcode: '',
})

watch(
  () => props.address,
  (addr) => {
    if (!addr) return
    Object.assign(addressForm, {
      unit_no: addr.unit_no ?? '',
      street_no: addr.street_no,
      street_name: addr.street_name,
      street_type: addr.street_type,
      city: addr.city,
      state: addr.state,
      postcode: addr.postcode,
    })
  },
  { immediate: true },
)

// Bridge addressForm ↔ AddressForm component
const addressFields = computed<AddressFields>({
  get: () => ({
    unit_no: addressForm.unit_no,
    street_no: addressForm.street_no,
    street_name: addressForm.street_name,
    street_type: addressForm.street_type,
    city: addressForm.city,
    state: addressForm.state,
    postcode: addressForm.postcode,
  }),
  set: (val) => Object.assign(addressForm, val),
})

function submitAddress() {
  addressForm.patch(organizationAddressPath(props.organization.id), { preserveScroll: true })
}

// ── Partners search & filter ───────────────────────────────────────────────
const ALL = '__all__'
const urlParams = new URLSearchParams(window.location.search)

const partnerSearch = ref(urlParams.get('q') ?? '')
const partnerActiveFilter = ref(urlParams.get('active') ?? ALL)

const hasPartnerFilters = computed(() => partnerSearch.value !== '' || partnerActiveFilter.value !== ALL)

const partnerFilterParams = computed(() => ({
  section: 'partners',
  q: partnerSearch.value || undefined,
  active: partnerActiveFilter.value !== ALL ? partnerActiveFilter.value : undefined,
}))

function applyPartnerFilters() {
  const params = Object.fromEntries(Object.entries(partnerFilterParams.value).filter(([, v]) => v != null))
  router.get(editOrganizationPath(props.organization.id), params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

let partnerSearchTimer: ReturnType<typeof setTimeout>

watch(partnerSearch, () => {
  clearTimeout(partnerSearchTimer)
  partnerSearchTimer = setTimeout(applyPartnerFilters, 350)
})

watch(partnerActiveFilter, applyPartnerFilters)

onUnmounted(() => clearTimeout(partnerSearchTimer))

function clearPartnerFilters() {
  partnerSearch.value = ''
  partnerActiveFilter.value = ALL
}

const partnerTableColumns: TableColumn[] = [
  { key: 'name', label: 'Name' },
  { key: 'contact', label: 'Contact' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'status', label: 'Status' },
]
</script>

<template>
  <Head :title="`Edit ${organization.name}`" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <OrganizationLayout
      v-model:active-section="activeSection"
      :organization-id="organization.id"
      :organization-name="organization.name"
    >
      <!-- ── Details section ───────────────────────────────────────────── -->
      <div v-show="activeSection === 'details'" class="space-y-6">
        <HeadingSmall
          title="Organization details"
          description="Update the organization's name and contact information"
        />

        <form class="space-y-4" @submit.prevent="submitDetails">
          <FormSection title="Contact Information">
            <div class="grid gap-2">
              <Label for="name">Name</Label>
              <Input id="name" v-model="detailsForm.name" placeholder="Acme Corp" required />
              <InputError :messages="detailsForm.errors.name" />
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div class="grid gap-2">
                <Label for="contact">Contact Person</Label>
                <Input id="contact" v-model="detailsForm.contact" placeholder="John Smith" required />
                <InputError :messages="detailsForm.errors.contact" />
              </div>
              <div class="grid gap-2">
                <Label for="phone">Phone</Label>
                <Input id="phone" v-model="detailsForm.phone" type="tel" placeholder="+61 400 000 000" required />
                <InputError :messages="detailsForm.errors.phone" />
              </div>
            </div>

            <div class="grid gap-2">
              <Label for="email">Email</Label>
              <Input id="email" v-model="detailsForm.email" type="email" placeholder="contact@acme.com" required />
              <InputError :messages="detailsForm.errors.email" />
            </div>
          </FormSection>

          <div class="border-border flex items-center justify-between rounded-lg border p-4">
            <div>
              <p class="text-sm font-medium">Active</p>
              <p class="text-muted-foreground text-xs">Allow this organization to manage partners and receive applications.</p>
            </div>
            <Switch id="active" v-model="detailsForm.active" />
          </div>

          <div class="flex items-center gap-4 pt-2">
            <Button type="submit" :disabled="detailsForm.processing">Save Changes</Button>
            <Transition
              enter-active-class="transition ease-in-out"
              enter-from-class="opacity-0"
              leave-active-class="transition ease-in-out"
              leave-to-class="opacity-0"
            >
              <p v-show="detailsForm.recentlySuccessful" class="text-muted-foreground text-sm">Saved.</p>
            </Transition>
          </div>
        </form>
      </div>

      <!-- ── Address section ───────────────────────────────────────────── -->
      <div v-show="activeSection === 'address'" class="space-y-6">
        <HeadingSmall title="Address" description="Update the organization's physical address" />

        <Deferred data="address">
          <template #fallback>
            <div class="space-y-4">
              <div class="grid grid-cols-3 gap-4">
                <Skeleton class="h-9" />
                <Skeleton class="h-9" />
                <Skeleton class="h-9" />
              </div>
              <Skeleton class="h-9" />
              <div class="grid grid-cols-3 gap-4">
                <Skeleton class="h-9" />
                <Skeleton class="h-9" />
                <Skeleton class="h-9" />
              </div>
            </div>
          </template>

          <form class="space-y-4" @submit.prevent="submitAddress">
            <FormSection title="Address">
              <AddressForm v-model="addressFields" :errors="addressForm.errors" />
            </FormSection>

            <div class="flex items-center gap-4 pt-2">
              <Button type="submit" :disabled="addressForm.processing">Save Address</Button>
              <Transition
                enter-active-class="transition ease-in-out"
                enter-from-class="opacity-0"
                leave-active-class="transition ease-in-out"
                leave-to-class="opacity-0"
              >
                <p v-show="addressForm.recentlySuccessful" class="text-muted-foreground text-sm">Saved.</p>
              </Transition>
            </div>
          </form>
        </Deferred>
      </div>

      <!-- ── Partners section ───────────────────────────────────────────── -->
      <div v-show="activeSection === 'partners'" class="space-y-4">
        <HeadingSmall title="Partners" description="All partners belonging to this organization" />

        <!-- Search & filters -->
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div class="relative flex-1">
            <Search class="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input v-model="partnerSearch" placeholder="Search by name, contact or email…" class="pl-9" />
          </div>
          <Select v-model="partnerActiveFilter">
            <SelectTrigger class="w-full sm:w-36">
              <SelectValue placeholder="Any status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="ALL">Any status</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Button
            v-if="hasPartnerFilters"
            variant="ghost"
            size="sm"
            class="text-muted-foreground hover:text-foreground shrink-0"
            @click="clearPartnerFilters"
          >
            <X class="size-4" />
            Clear
          </Button>
        </div>

        <Deferred data="partners">
          <template #fallback>
            <div class="space-y-2">
              <Skeleton class="h-10 w-full" />
              <Skeleton class="h-10 w-full" />
              <Skeleton class="h-10 w-full" />
            </div>
          </template>

          <div
            v-if="!partners || partners.records.length === 0"
            class="flex flex-col items-center gap-3 py-10 text-center"
          >
            <Users class="text-muted-foreground/40 size-10" />
            <p class="text-muted-foreground text-sm">
              {{ hasPartnerFilters ? 'No partners match your filters' : 'No partners yet' }}
            </p>
            <Button v-if="hasPartnerFilters" variant="outline" size="sm" @click="clearPartnerFilters">
              <X class="size-4" />
              Clear filters
            </Button>
          </div>

          <div v-else class="rounded-lg border">
            <div class="border-border flex items-center justify-between border-b px-4 py-2">
              <span class="text-muted-foreground text-xs">
                {{ partners.pagination.total_count }} partner{{ partners.pagination.total_count !== 1 ? 's' : '' }}
                {{ hasPartnerFilters ? 'found' : 'total' }}
              </span>
              <SlidersHorizontal class="text-muted-foreground size-3.5" />
            </div>
            <AppDataTable :columns="partnerTableColumns" compact>
              <tr
                v-for="partner in partners.records"
                :key="partner.id"
                class="border-border hover:bg-muted/50 border-b transition-colors last:border-0"
              >
                <td class="px-4 py-3 font-medium">
                  <Link
                    :href="editPartnerPath(partner.id)"
                    class="text-foreground hover:text-primary hover:underline"
                    >{{ partner.name }}</Link
                  >
                </td>
                <td class="text-muted-foreground px-4 py-3">{{ partner.contact }}</td>
                <td class="text-muted-foreground px-4 py-3">{{ partner.email }}</td>
                <td class="text-muted-foreground px-4 py-3">{{ partner.phone }}</td>
                <td class="px-4 py-3">
                  <Badge :variant="partner.active ? 'default' : 'secondary'">
                    {{ partner.active ? 'Active' : 'Inactive' }}
                  </Badge>
                </td>
              </tr>
            </AppDataTable>
            <AppPagination
              :current-page="partners.pagination.current_page"
              :total-pages="partners.pagination.total_pages"
              :total-count="partners.pagination.total_count"
              :per-page="partners.pagination.per_page"
              :base-url="editOrganizationPath(organization.id)"
              :extra-params="partnerFilterParams"
            />
          </div>
        </Deferred>
      </div>
    </OrganizationLayout>

    <!-- Activity & Comments — shown under details section -->
    <div v-if="activeSection === 'details'" class="flex max-w-7xl flex-col px-4 pb-6 lg:flex-row lg:space-x-12">
      <div class="hidden lg:block lg:w-48 lg:shrink-0" />
      <div class="flex-1">
        <Deferred data="comments">
          <template #fallback>
            <div class="mt-8 space-y-2">
              <div class="bg-muted h-4 w-48 animate-pulse rounded" />
              <div class="bg-muted h-16 animate-pulse rounded" />
              <div class="bg-muted h-16 animate-pulse rounded" />
            </div>
          </template>
          <CommentThread
            :comments="comments ?? []"
            :create-path="organizationCommentsPath(organization.id)"
            :can-comment="true"
          />
        </Deferred>
      </div>
    </div>
  </AppLayout>
</template>
